@use '../../styles/tokens' as *;

// Holds the fixed square stage (PREVIEW_STAGE_SIZE in index.tsx), with room for the checkerboard
// around a preview that overflows it.
$previewColumnWidth: 500px;
$purchaseColumnWidth: 540px;
$dialogViewportGutter: 20px;

.dialog {
  width: $purchaseColumnWidth + $previewColumnWidth;
  max-width: calc(100vw - #{$dialogViewportGutter * 2});
  left: 50%;
  transform: translateX(-50%);
  top: 100px;
  z-index: 1001;
}

.narrowDialog {
  width: 600px;
}

.dialogContent {
  padding: 0;
  // Keeps the purchase column the only scroller; otherwise this box scrolls too and the modal
  // opens with its contents clipped.
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  min-height: 0;
}

.content {
  display: flex;
  align-items: stretch;
  max-height: 760px;
  min-height: 300px;

  a {
    color: $colorBrightBlue;
  }

  p {
    margin-bottom: 0;
    line-height: 1.4;
  }

  // Preserve paragraph spacing only inside callouts; flex containers use gap instead
  :global(.bp5-callout) {
    p + p {
      margin-top: $space0_75;
    }
  }
}

.bulkOrderModalContent {
  overflow-y: auto;

  :global(.bp5-multistep-dialog-panels) {
    height: 500px;
  }

  :global(.bp5-multistep-dialog-right-panel) {
    display: flex;
    flex-direction: column;
    padding: $space1;
    gap: $gapDefault;
  }

  :global(.bp5-dialog-body) {
    flex: 1;
    min-height: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  :global(.bp5-dialog-footer) {
    background-color: transparent;
    border-top: none;
  }
}

.loadingSpinner {
  flex: 1;
  margin-top: -20px;
}

.errorMessage {
  margin-top: -20px;
}

.sceneCoverageFailed {
  margin-top: 40px;
}

.purchaseInfoContainer {
  padding: $paddingDefault 1.25rem $paddingDefault $paddingDefault;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.purchaseInfoDescription {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: $gapDefault;
}

.purchaseInfoActions {
  // Pinned to the bottom of the scrolling column so the order action is always in reach, and
  // the fade over whatever sits beneath it says there is more to scroll to.
  position: sticky;
  bottom: 0;
  margin-top: $marginBlockSection;
  padding: $space1_5 0 $marginBlockDefault;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: $gapDefault;
  background: linear-gradient(to bottom, rgba($colorWhite, 0), $colorWhite $space1_5);
}

.inCartNotice {
  margin-top: $marginBlockDefault;
  text-align: center;
}

.cartHelp {
  margin-top: $marginBlockDefault;
  text-align: center;
  font-size: 1.2rem;
  color: $colorDarkestGray;

  // The region stays mounted with nothing to say so that later text is announced, so it has to
  // take no space while it is empty.
  &:empty {
    margin: 0;
  }
}

.cartRefusal {
  color: $colorDarkRed;
}

.previewArea {
  flex: 0 1 $previewColumnWidth;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 0;
  border-left: 1px solid $colorGray;
}

.previewFooter {
  flex: 1;
  padding: $paddingSnug;
}

.previewDescription {
  text-align: center;
  padding: 0.8rem $paddingDefault;
  margin-bottom: $marginBlockDefault;
}

.previewImageContainer {
  padding: $paddingDefault;
  border-bottom: 1px solid $colorGray;

  // checkerboard background
  background-color: $colorDarkGray;
  background-image:
    linear-gradient(45deg, $colorGray 25%, transparent 25%),
    linear-gradient(-45deg, $colorGray 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, $colorGray 75%),
    linear-gradient(-45deg, transparent 75%, $colorGray 75%);
  background-size: 40px 40px;
  background-position:
    0 0,
    0 20px,
    20px -20px,
    -20px 0px;

  position: relative;
  display: flex;
  flex: 1;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
}

.previewImageWrapper {
  position: relative;
}

.previewImage {
  display: block;
  position: relative;
}

.grayscale {
  filter: grayscale(100%);
}

.previewFeature {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sourceResolution {
  font-weight: bold;
}

.separator {
  margin: $marginBlockSnug 0;
  border-top: 1px solid $colorLightGray;
}

.loadingSpinnerContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $gapDefault;
  width: 100%;
  height: 100%;
}

.panelContent {
  display: flex;
  flex-direction: column;
  gap: $gapDefault;
  flex: 1;
  min-height: 0;

  > :last-child {
    margin-top: auto;
  }
}

.columnPanel {
  flex-direction: column;
  gap: $gapDefault;
  flex: 1;
  min-height: 0;
}

.tableContainer {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid $colorLightGray;
  border-radius: 6px;

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  th {
    background-color: $colorLightestBlue;
    color: $colorDarkBlue;
    font-weight: 600;
    padding: $space0_5 $space0_75;
    text-align: left;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    padding: $space0_5 $space0_75;
    background-color: white;
    border-bottom: 1px solid $colorLighterGray;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.typeSelector {
  margin-bottom: $marginBlockDefault;
}

.disabledOptionHint {
  margin-top: $marginBlockSnug;
  margin-bottom: 0;
  font-size: 1rem;
  color: $colorDarkGray;
  display: flex;
  align-items: center;
  gap: $gapExtraSnug;
}
